import React, { useCallback, useState, useMemo, useEffect, type FC, type ChangeEvent } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import debounce from 'lodash/debounce';

import { Box } from '@atlaskit/primitives/compiled';
import Textfield, { type TextFieldProps } from '@atlaskit/textfield';
import SearchIcon from '@atlaskit/icon/core/search';
import SelectClearIcon from '@atlaskit/icon/core/cross-circle';
import { IconButton } from '@atlaskit/button/new';

import { useEventUtils } from './eventsUtils';

export type AnalyticsSearchFieldProps = {
	onSearch: (term: string) => void;
	placeholder?: string;
	isDisabled?: boolean;
	isClearable?: boolean;
	tableName: string;
	level: string;
	tabName: string;
	source: string;
} & Pick<TextFieldProps, 'isCompact' | 'appearance'>;

const i18n = defineMessages({
	analyticsSearchFieldSearchIconLabel: {
		id: 'confluence-analytics.search-field.search-icon-label',
		defaultMessage: 'Search by text',
		description: 'The A11Y label for search icon on the search field.',
	},
	analyticsSearchFieldClearIconLabel: {
		id: 'confluence-analytics.search-field.clear-icon-label',
		defaultMessage: 'Clear the search field',
		description:
			'The A11Y label for clear icon on the search field which removes the search entry.',
	},
});

export const AnalyticsSearchField: FC<AnalyticsSearchFieldProps> = ({
	onSearch,
	isDisabled,
	isClearable,
	isCompact,
	appearance,
	placeholder,
	tableName,
	level,
	tabName,
	source,
}) => {
	const { formatMessage } = useIntl();
	const { fireTableSearchFilterChangedEvent } = useEventUtils();
	const [searchTerm, setSearchTerm] = useState('');

	const debouncedSearch = useMemo(() => debounce(onSearch, 250), [onSearch]);

	useEffect(() => {
		return () => {
			debouncedSearch.cancel();
		};
	}, [debouncedSearch]);

	const setNewSearchFilter = useCallback(
		(newValue: string) => {
			const isCleared = !newValue;
			fireTableSearchFilterChangedEvent({
				isCleared,
				tableName,
				level,
				tabName,
				source,
			});

			setSearchTerm(newValue);
			debouncedSearch(newValue);
		},
		[fireTableSearchFilterChangedEvent, level, source, tableName, tabName, debouncedSearch],
	);

	const handleSearchFilterChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			setNewSearchFilter(e.target.value);
		},
		[setNewSearchFilter],
	);

	const handleClearSearchFilter = useCallback(() => {
		setNewSearchFilter('');
	}, [setNewSearchFilter]);

	return (
		<Textfield
			isCompact={isCompact}
			appearance={appearance}
			isDisabled={isDisabled}
			placeholder={placeholder}
			value={searchTerm}
			onChange={handleSearchFilterChange}
			elemBeforeInput={
				<Box paddingInlineStart="space.100">
					<SearchIcon label={formatMessage(i18n.analyticsSearchFieldSearchIconLabel)} />
				</Box>
			}
			elemAfterInput={
				isClearable && Boolean(searchTerm) ? (
					<Box paddingInlineEnd="space.050">
						<IconButton
							spacing="compact"
							appearance="subtle"
							label={formatMessage(i18n.analyticsSearchFieldClearIconLabel)}
							icon={(iconProps) => <SelectClearIcon {...iconProps} />}
							onClick={handleClearSearchFilter}
						/>
					</Box>
				) : undefined
			}
		/>
	);
};
